import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { formatPayModDate } from "../../../../services/planChange";
import { HiChevronRight } from "react-icons/hi";
import NoTranslate from "../../../atoms/NoTranslate";
import StatusBadge from "../../../atoms/StatusBadge";
import {
  CurrentPeriod,
  PaymentHistoryGroup,
  formatAmount,
  resolveCurrentPeriod,
  summarizePlans,
} from "./paymentHistoryUtils";
import {
  getPlanTypeLabel,
  periodSentence,
  planBadgeStyle,
  planTitle,
} from "./paymentHistoryDisplay";

interface PaymentStatusSummaryProps {
  paymentHistory: PaymentHistoryGroup[];
  onOpenPlan?: (planCode: string) => void;
}

const PaymentStatusSummary: React.FC<PaymentStatusSummaryProps> = ({
  paymentHistory,
  onOpenPlan,
}) => {
  const { t } = useTranslation();

  const plans = useMemo(() => summarizePlans(paymentHistory), [paymentHistory]);
  const periodByPlan = useMemo(() => {
    const map = new Map<string, CurrentPeriod | null>();
    paymentHistory.forEach((g) => map.set(g.planCode, resolveCurrentPeriod(g)));
    return map;
  }, [paymentHistory]);

  if (plans.length === 0) return null;

  return (
    <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-200">
        <p className="text-xs font-normal text-gray-500">
          {t("paymentHistory.summary.titleOn", {
            date: moment().format("LL"),
          })}
        </p>
      </div>

      <ul className="divide-y divide-gray-100">
        {plans.map((plan) => {
          const style = planBadgeStyle(plan.kind, plan.state);
          const isDegraded =
            plan.state === "paymentFailed" || plan.state === "suspended";
          const period = periodByPlan.get(plan.planCode) ?? null;
          const periodLine = periodSentence(period, t);

          return (
            <li
              key={plan.planCode}
              className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <span
                className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${style.dot}`}
                aria-hidden
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {onOpenPlan ? (
                    <button
                      type="button"
                      onClick={() => onOpenPlan(plan.planCode)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-blue-700 transition-colors text-left"
                      title={t("paymentHistory.timeline.open") as string}
                    >
                      <NoTranslate>
                        {planTitle(plan.planDescription, t)}
                      </NoTranslate>
                      <HiChevronRight size={14} className="text-gray-400" />
                    </button>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">
                      <NoTranslate>
                        {planTitle(plan.planDescription, t)}
                      </NoTranslate>
                    </span>
                  )}

                  <StatusBadge color={style.tone}>
                    {plan.state
                      ? t(`paymentHistory.summary.state.${plan.state}`)
                      : getPlanTypeLabel(plan.paymentType, t)}
                  </StatusBadge>
                </div>

                <p className="text-xs text-gray-500 mt-0.5">
                  {(isDegraded ? null : periodLine) ??
                    (t("paymentHistory.summary.lastActivity", {
                      date: formatPayModDate(plan.latestStepDate, "ll"),
                    }) as string)}
                </p>
              </div>

              <div className="text-right flex-shrink-0">
                {plan.amount > 0 && (
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    <NoTranslate>
                      {formatAmount(plan.amount, plan.currency)}
                    </NoTranslate>
                  </p>
                )}
                <p className="text-xs text-gray-400 tabular-nums">
                  {t("paymentHistory.timeline.title", {
                    count: plan.movements,
                  })}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PaymentStatusSummary;
